import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import compact from 'lodash/compact'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import UserSelector from 'modules/core/components/UserSelector'
import { Fields } from 'modules/core/components/UserSelector/_filters/filterFields'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import type { Users } from 'modules/core/store/collections/users'
import type AccessGroup from 'modules/core/store/models/AccessGroup'

import type { Employees } from 'modules/employees/store/collections/employees'
import { useEngagementContext } from 'modules/engagement/hooks/useEngagementContext'
import { computeAvailableUsers } from 'modules/surveys/components/New/AccessGroupPicker/ManualSelection'
import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import formAccessGroupMemberships from 'modules/surveys/store/collections/formAccessGroupMemberships'
import questionGroups from 'modules/surveys/store/collections/questionGroups'
import { Status } from 'modules/surveys/store/models/v2/Survey'

type UserSelectorProps = React.ComponentProps<typeof UserSelector>

interface Props {
  accessGroup: AccessGroup
  surveyId: number
  surveyStatus: string
  back: string
  users: Users
  employees: Employees
}

const ManualNewRespondent = ({
  accessGroup,
  surveyId,
  surveyStatus,
  back,
  users,
  employees,
}: Props) => {
  const { allMyDescendants } = useCurrentSession()
  const { updateCanBeStarted } = useSurveyShowContext()
  const { canCreateEverybody, canCreateReportees, canCreateTeam, canCreateOwned } =
    useEngagementContext()
  const [userIds, setUserIds] = React.useState<number[]>([])
  const [running, setRunning] = React.useState(false)
  const navigate = useNavigate()
  const onClose = () => navigate(back)
  const onSelectMultiple: UserSelectorProps['onSelectMultiple'] = (selectedIds) =>
    setUserIds(selectedIds)
  const {
    access: { id: userId },
  } = useCurrentSession()

  const createAccessGroupMemberships = async () =>
    await formAccessGroupMemberships.bulkCreate({
      access_ids: userIds,
      access_group_id: accessGroup.attributes.id,
      form_id: surveyId,
    })

  const createQuestionGroups = async () =>
    await questionGroups.bulkCreate({
      form_id: surveyId,
      accesses_ids: userIds,
      access_group_id: accessGroup.attributes.id,
    })

  const submit = async (closePortal: ClosePortalFn) => {
    setRunning(true)

    const { success } = await asyncAction(() =>
      surveyStatus === Status.draft ? createAccessGroupMemberships() : createQuestionGroups()
    )
    setRunning(false)

    if (success) {
      if (surveyStatus !== Status.draft) {
        formAccessGroupMemberships.fetch({
          data: {
            form_id: surveyId,
            access_group_id: accessGroup.attributes.id,
          },
        })
      }
      if (surveyStatus !== Status.active) await updateCanBeStarted()
      closePortal()
    }
  }

  formAccessGroupMemberships.fetch({
    data: {
      access_group_id: accessGroup.attributes.id,
      form_id: surveyId,
    },
  })

  const availableUsers = React.useRef(
    computeAvailableUsers(
      users,
      employees,
      userId,
      false,
      allMyDescendants,
      canCreateEverybody,
      canCreateReportees,
      canCreateTeam,
      canCreateOwned
    )
  ).current
  const memberIds = formAccessGroupMemberships.map((m) => m.attributes.access_id)

  const notMembers = availableUsers.filter((user) => !memberIds.includes(user.get('id')))

  return (
    <Modal
      title={i18n.t('surveys.form.participants.new.title')}
      description={i18n.t('surveys.form.participants.new.description.survey')}
      illustration="surveys"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <Button disabled={!userIds.length || running} type="primary" submit>
          {i18n.t('surveys.form.participants.new.submit')}
        </Button>
      }
    >
      <UserSelector
        users={notMembers}
        onSelect={(id) => setUserIds(xor(userIds, compact([id])))}
        onSelectMultiple={onSelectMultiple}
        selectedIds={userIds}
        hideFields={[Fields.EmployeeStatus, Fields.UserType]}
      />
    </Modal>
  )
}

export default observer(ManualNewRespondent)
